import { coreQueryKey } from '@/api/core/authorization';
import {
  patchText,
  readPresentation,
  writePresentation,
  type Component,
  type PresentationBody,
  type Purpose,
  type Resource,
  type TextValue,
} from '@/api/core/native-presentation';
import {
  readNativeCatalog,
  type NativeCatalog,
  type NativeCatalogRoom,
  type NativeCatalogUnit,
  type NativePropertyBasic,
  type NativeTypeDetails,
} from '@/api/core/native-property-read';
import { readPropertyCatalog } from '@/api/core/properties';
import { queryClient } from '@/api/query-client';
import { DirtyGuard } from '@/components/dirty-guard';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DirtyConfirmDialog,
  FormField,
  Input,
  useDirtyConfirm,
} from '@mgrvtechgroup/admin-ds';
import { useEffect, useRef, useState } from 'react';
import { EditActionBar } from '../edit-action-bar';
import { invalidateHomepagePreview } from '../homepage-preview-cache';
import type { PropertyRuntime } from '../property-queries';
import { describeChanges } from './presentation-changes';
import { PresentationEditForm } from './presentation-edit-form';
import { PresentationSummary } from './presentation-summary';
import { useCurrentCommand } from './use-current-command';
const CATALOG_REUSE_MS = 30_000;
/**
 * 화면이 방금 읽어 둔 지점 catalog 첫 페이지에 대상이 있으면 소속 확인에 그대로 쓴다.
 * 같은 데이터를 다시 읽지 않기 위한 것이며, 쓰기는 어차피 서버가 revision(CAS)으로 다시 검사한다.
 * 캐시가 없거나 오래됐거나 대상이 첫 페이지에 없으면 false를 돌려 기존처럼 서버에서 확인한다.
 */
export function catalogShowsTarget(
  runtime: PropertyRuntime,
  propertyId: string,
  resource: Resource,
  id: string,
  now = Date.now(),
) {
  const generation = runtime.session.getSnapshot().generation;
  const queries = queryClient.getQueryCache().findAll({
    queryKey: coreQueryKey(runtime.apiNamespace, generation, 'native-property-catalog', propertyId),
  });
  return queries.some((query) => {
    const page = query.state.data as { data?: NativeCatalog } | undefined;
    const c = page?.data;
    if (!c || now - query.state.dataUpdatedAt > CATALOG_REUSE_MS) return false;
    if (c.property.id !== propertyId) return false;
    return (
      (resource === 'properties' && id === propertyId) ||
      (resource === 'room-types' &&
        c.room_types.some((t) => t.id === id && t.property_id === propertyId)) ||
      (resource === 'rooms' && c.rooms.some((r) => r.id === id && r.property_id === propertyId)) ||
      (resource === 'units' &&
        c.rooms.some(
          (r) =>
            r.property_id === propertyId && r.units.some((u) => u.id === id && u.room_id === r.id),
        ))
    );
  });
}
export async function verifyPresentationParent(
  runtime: PropertyRuntime,
  propertyId: string,
  resource: Resource,
  id: string,
) {
  let cursor: string | null = null;
  const seen = new Set<string>();
  do {
    const page = await readNativeCatalog(runtime.client, propertyId, cursor);
    const c = page.data;
    if (c.property.id !== propertyId) throw new Error('지점 범위가 변경되었습니다.');
    if (
      (resource === 'properties' && id === propertyId) ||
      (resource === 'room-types' &&
        c.room_types.some((t) => t.id === id && t.property_id === propertyId)) ||
      (resource === 'rooms' && c.rooms.some((r) => r.id === id && r.property_id === propertyId)) ||
      (resource === 'units' &&
        c.rooms.some(
          (r) =>
            r.property_id === propertyId && r.units.some((u) => u.id === id && u.room_id === r.id),
        ))
    )
      return;
    cursor = page.next_cursor;
    if (cursor && seen.has(cursor)) break;
    if (cursor) seen.add(cursor);
  } while (cursor);
  throw new Error('현재 지점의 대상 관계를 확인하지 못했습니다.');
}
/** Decoded native hierarchy reused for presentation read/write membership checks. */
export interface NativePresentationScope {
  property: NativePropertyBasic;
  type?: NativeTypeDetails;
  room?: NativeCatalogRoom;
  unit?: NativeCatalogUnit;
}
function nativeScopeShowsTarget(
  scope: NativePresentationScope,
  propertyId: string,
  resource: Resource,
  id: string,
) {
  if (scope.property.id !== propertyId) return false;
  if (resource === 'properties') return id === propertyId;
  const type = scope.type;
  if (type?.property_id !== propertyId) return false;
  if (resource === 'room-types') return type.id === id;
  const room = scope.room;
  if (room?.property_id !== propertyId || room.room_type_id !== type.id) return false;
  if (resource === 'rooms') return room.id === id;
  return (
    scope.unit?.id === id &&
    scope.unit.room_id === room.id &&
    room.units.some((unit) => unit.id === id && unit.room_id === room.id)
  );
}
export function PresentationEditor({
  runtime,
  propertyId,
  resource,
  id,
  label,
  legacyProperty = false,
  nativeScope,
}: {
  legacyProperty?: boolean;
  nativeScope?: NativePresentationScope;
  runtime: PropertyRuntime;
  propertyId: string;
  resource: Resource;
  id: string;
  label: string;
}) {
  const scope = `presentation:${propertyId}:${resource}:${id}`;
  const cmd = useCurrentCommand({
    runtime,
    scope,
    bits: 4,
    read: () => readPresentation(runtime.client, resource, id),
    write: (body: PresentationBody) => writePresentation(runtime.client, resource, id, body),
    validate: async () => {
      if (legacyProperty) {
        let cursor: string | null = null;
        const seen = new Set<string>();
        let found = false;
        do {
          const c = await readPropertyCatalog(runtime.client, propertyId, { room: cursor });
          if (c.property.property_id !== propertyId || c.property.sourceDeletedMarker !== null)
            break;
          found =
            (resource === 'properties' && id === propertyId) ||
            (resource === 'room-types' &&
              c.roomTypes.some((t) => t.id === id && t.sourceDeletedMarker === null)) ||
            (resource === 'rooms' &&
              c.rooms.some((r) => r.id === id && r.sourceDeletedMarker === null)) ||
            (resource === 'units' &&
              c.slots.some(
                (u) =>
                  u.id === id &&
                  u.sourceDeletedMarker === null &&
                  c.rooms.some((r) => r.id === u.roomId && r.sourceDeletedMarker === null),
              ));
          if (found) break;
          cursor = c.roomNextCursor;
          if (cursor && seen.has(cursor)) break;
          if (cursor) seen.add(cursor);
        } while (cursor);
        if (!found) throw new Error('현재 지점 범위를 확인하지 못했습니다.');
      } else if (nativeScope) {
        if (!nativeScopeShowsTarget(nativeScope, propertyId, resource, id))
          throw new Error('현재 지점의 대상 관계를 확인하지 못했습니다.');
      } else if (!catalogShowsTarget(runtime, propertyId, resource, id))
        await verifyPresentationParent(runtime, propertyId, resource, id);
    },
  });
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [texts, setTexts] = useState<TextValue[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [removed, setRemoved] = useState<number[]>([]);
  const [removedComponents, setRemovedComponents] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const confirmRegion = useRef<HTMLDivElement>(null);
  const confirmTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirm) {
      confirmRegion.current?.focus();
      if (typeof confirmRegion.current?.scrollIntoView === 'function')
        confirmRegion.current.scrollIntoView({ block: 'start' });
    }
  }, [confirm]);
  const [newPurpose, setNewPurpose] = useState<Purpose>('DISPLAY_NAME');
  const [language, setLanguage] = useState<TextValue['language']>('ko');
  const currentValue = cmd.current;
  const [draftSource, setDraftSource] = useState(currentValue);
  // Prepare the draft before exposing inputs for a newly read current value.
  // Deferring this lets an empty draft replace untouched content or overwrite edits.
  if (currentValue !== draftSource) {
    setDraftSource(currentValue);
    if (currentValue) {
      setName(currentValue.native_name ?? '');
      setTexts(
        currentValue.texts.filter((t) => !t.group_ref && !t.media_ref && !t.source_item_key),
      );
      setComponents(currentValue.components);
      setRemoved([]);
      setRemovedComponents([]);
      setReason('');
      setConfirm(false);
    }
  }
  const locked = cmd.busy || !!cmd.attempt || !cmd.canWrite;
  const current = cmd.current;
  function build(): PresentationBody {
    if (!current) throw new Error('현재값을 먼저 확인해 주세요.');
    return {
      command_id: crypto.randomUUID(),
      expected: current.expected,
      values: {
        native_name:
          name === (current.native_name ?? '') ? null : { operation: 'SET', value: name },
        presentation: {
          texts: texts.map((t, i) =>
            patchText(
              t,
              removed.includes(i) ||
                (t.component_key && removedComponents.includes(t.component_key))
                ? 'REMOVE'
                : 'SET',
            ),
          ),
          components: components.map((c) => ({
            operation: c.key && removedComponents.includes(c.key) ? 'REMOVE' : 'SET',
            key: c.key,
            value: c.key && removedComponents.includes(c.key) ? null : c,
          })),
        },
      },
      reason,
      publication: 'APPLY_CURRENT',
    };
  }
  const editableTexts =
    current?.texts.filter((t) => !t.group_ref && !t.media_ref && !t.source_item_key) ?? [];
  const dirty =
    editing &&
    !!current &&
    (name !== (current.native_name ?? '') ||
      JSON.stringify(texts) !== JSON.stringify(editableTexts) ||
      JSON.stringify(components) !== JSON.stringify(current.components) ||
      removed.length > 0 ||
      removedComponents.length > 0 ||
      !!reason.trim());
  const changes = current
    ? describeChanges({ current, name, texts, removed, components, removedComponents })
    : [];
  const dirtyGuard = useDirtyConfirm(dirty);
  const saved = cmd.success;
  useEffect(() => {
    if (!saved) return;
    // 저장한 문구가 홈페이지 미리보기에 바로 보이도록 짧게 캐시된 공개 조회를 낡은 것으로 표시한다.
    void invalidateHomepagePreview(queryClient);
    queueMicrotask(() => {
      setEditing(false);
    });
  }, [saved]);
  const editTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (editing) document.getElementById(`${scope}-name`)?.focus();
  }, [editing, scope]);
  function cancelEditing() {
    dirtyGuard.guardedClose(() => {
      if (!current) return;
      setName(current.native_name ?? '');
      setTexts(editableTexts);
      setComponents(current.components);
      setRemoved([]);
      setRemovedComponents([]);
      setReason('');
      setConfirm(false);
      setEditing(false);
      queueMicrotask(() => editTrigger.current?.focus());
    });
  }

  // 저장 결과·오류는 수정 창이 열려 있을 때 창 안에서 보여 준다(창 뒤 페이지는 가려진다).
  const refreshButton = (
    <Button variant="outline" disabled={cmd.busy} onClick={() => void cmd.refresh()}>
      {cmd.attempt?.accepted ? '저장 후 현재값 다시 조회' : '현재값 다시 조회'}
    </Button>
  );
  const messages = (
    <>
      {cmd.error && <p role="alert">{cmd.error}</p>}
      {cmd.success && <p role="status">저장 후 같은 대상의 현재값을 다시 확인했습니다.</p>}
      {!cmd.canWrite && <p>표시 콘텐츠 권한이 필요합니다.</p>}
      {cmd.attempt && !cmd.attempt.accepted && (
        <div role="alert">
          <p>요청 결과가 불명확합니다. 원래 요청과 변경 기준을 보존했습니다.</p>
          <Button disabled={cmd.busy} onClick={() => void cmd.submit()}>
            원래 요청으로 결과 확인
          </Button>
        </div>
      )}
    </>
  );
  const dialogOpen = editing && !!current;

  return (
    <section aria-label="콘텐츠 편집" className="space-y-4 rounded-md border p-4">
      {editing && <DirtyGuard includeSearch when={dirty} />}
      <DirtyConfirmDialog
        open={dirtyGuard.confirmOpen}
        onOpenChange={(next) => {
          if (!next) dirtyGuard.onCancel();
        }}
        onConfirm={dirtyGuard.onConfirm}
      />
      <h3 className="title-md">{label} 콘텐츠</h3>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {dialogOpen ? <span /> : refreshButton}
        {current && (
          <Button
            ref={editTrigger}
            variant="outline"
            disabled={locked}
            aria-label="콘텐츠 수정"
            onClick={() => {
              setEditing(true);
            }}
          >
            수정
          </Button>
        )}
      </div>
      {!dialogOpen && messages}
      {current && (
        <>
          <p>현재 관리 이름: {current.native_name ?? '미설정'}</p>
          <PresentationSummary current={current} />
          <Dialog
            open={editing}
            onOpenChange={(next) => {
              if (!next && !cmd.busy) cancelEditing();
            }}
          >
            <DialogContent className="sm:max-w-3xl">
              <DialogHeader>
                <DialogTitle>{label} 콘텐츠 수정</DialogTitle>
                <DialogDescription>
                  저장하면 현재값에 바로 반영됩니다. 사진 선택·게시는 별도입니다.
                </DialogDescription>
              </DialogHeader>
              <DialogBody className="max-h-[70vh] space-y-4 overflow-y-auto">
                {refreshButton}
                {messages}
                <p className="body-sm text-muted-foreground">
                  관리 이름 변경 사유: {current.native_change?.reason ?? '기록 없음'} · 표시 콘텐츠
                  변경 사유: {current.presentation_change?.reason ?? '기록 없음'}
                </p>
                <PresentationEditForm
                  scope={scope}
                  locked={locked}
                  name={name}
                  onName={(value) => {
                    setName(value);
                    setConfirm(false);
                  }}
                  texts={texts}
                  setTexts={setTexts}
                  removed={removed}
                  setRemoved={setRemoved}
                  components={components}
                  setComponents={setComponents}
                  removedComponents={removedComponents}
                  setRemovedComponents={setRemovedComponents}
                  newPurpose={newPurpose}
                  setNewPurpose={setNewPurpose}
                  language={language}
                  setLanguage={setLanguage}
                  touch={() => {
                    setConfirm(false);
                  }}
                />
                <FormField label="변경 사유" htmlFor={`${scope}-reason`}>
                  <Input
                    id={`${scope}-reason`}
                    value={reason}
                    maxLength={500}
                    disabled={locked}
                    onChange={(e) => {
                      setReason(e.target.value);
                      setConfirm(false);
                    }}
                  />
                </FormField>
                {confirm && (
                  <div
                    ref={confirmRegion}
                    tabIndex={-1}
                    role="region"
                    aria-label="변경 영향 확인"
                    className="scroll-mt-48 space-y-3 rounded border p-3"
                  >
                    <h4 className="font-semibold">바뀐 값</h4>
                    {changes.length === 0 ? (
                      <p>바뀐 값이 없습니다. 사유만 기록하고 현재값을 다시 적용합니다.</p>
                    ) : (
                      <ul className="space-y-1">
                        {changes.map((change) => (
                          <li key={change.id}>
                            {change.label}: {change.before} → {change.after}
                          </li>
                        ))}
                      </ul>
                    )}
                    <p>
                      이 대상에 연결되어 보이는 사진 {current.impact.visible_media_assignments}장 ·
                      공간 안내에 연결된 항목 {current.impact.component_reference_collections}개
                    </p>
                    <p>사유: {reason}</p>
                    <Button disabled={locked} onClick={() => void cmd.submit(build())}>
                      확인하고 현재값에 저장
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setConfirm(false);
                        confirmTrigger.current?.focus();
                      }}
                    >
                      수정 계속
                    </Button>
                  </div>
                )}
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" disabled={locked} onClick={cancelEditing}>
                  수정 취소
                </Button>
                <EditActionBar label="이름·표시 저장 동작">
                  <Button
                    ref={confirmTrigger}
                    disabled={
                      locked ||
                      !reason.trim() ||
                      (!name && current.native_name !== null) ||
                      Array.from(name).length > 200
                    }
                    onClick={() => {
                      setConfirm(true);
                    }}
                  >
                    변경 내용·영향 확인
                  </Button>
                </EditActionBar>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </section>
  );
}
